import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

// カスタムフォームで使用するHTML群を集約

/**
 * テキストボックス
 * @param {object} 質問、必須フラグ、回答、プレースホルダー、形式、配列の番号、セッター
 * @returns テキストボックスのHTML
 */
function TextBox({ question, need, answer, placeholder, inputType, num, setter }) {
  return (
    <input
      type="text"
      value={answer[0]}
      placeholder={placeholder}
      name={`${inputType}`}
      onChange={(e) => {
        const obj = { question, inputType, answer: [e.target.value], require: need };
        setter(obj, num);
      }}
    />
  );
}
TextBox.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  placeholder: PropTypes.string,
  inputType: PropTypes.number,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * テキストエリア
 * @param {object} 質問、必須フラグ、回答、プレースホルダー、形式、配列の番号、セッター
 * @returns テキストエリアのHTML
 */
function TextArea({ question, need, answer, placeholder, inputType, num, setter }) {
  return (
    <textarea
      placeholder={placeholder}
      value={answer[0]}
      onChange={(e) => {
        const obj = { question, inputType, answer: [e.target.value], require: need };
        setter(obj, num);
      }}
    />
  );
}
TextArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  placeholder: PropTypes.string,
  inputType: PropTypes.number,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * 日付入力欄
 * @param {object} 質問、必須フラグ、回答、プレースホルダー、形式、配列の番号、セッター
 * @returns 日付入力のHTML
 */
function DateArea({ question, need, answer, inputType, num, setter }) {
  const yearRef = useRef(null);
  const monthRef = useRef(null);
  const dayRef = useRef(null);

  const dateArray = answer.length !== 0 ? answer[0].split('-') : '';
  const year = dateArray[0] || '';
  const month = dateArray[1] || '';
  const day = dateArray[2] || '';

  const intoSetter = (value) => {
    const newDay = answer[0] !== '--' ? [`${year}-${month}-${value}`] : [''];
    const obj = {
      question,
      inputType,
      answer: newDay,
      require: need,
    };
    setter(obj, num);
  };

  // 回答が["--"]の時は空文字に修正する。
  useEffect(() => {
    if (answer[0] === '--') {
      const obj = {
        question,
        inputType,
        answer: [''],
        require: need,
      };
      setter(obj, num);
    }
  }, [question, inputType, answer, need, num, setter]);

  return (
    <div className="row">
      <input
        className="hide-spin"
        type="text"
        id="year"
        name="year"
        maxLength="4"
        style={{ width: '6rem' }}
        value={year}
        ref={yearRef}
        inputMode="numeric"
        onChange={(e) => {
          // 入力時に数字以外を入力できなくする
          if (/^\d*$/.test(e.target.value)) {
            const obj = {
              question,
              inputType,
              answer: [`${e.target.value}-${month}-${day}`],
              require: need,
            };
            setter(obj, num);
            if (e.target.value.length === 4) {
              monthRef.current.focus();
            }
          }
        }}
        onBlur={(e) => {
          const newYear =
            e.target.value.length < 4 && e.target.value !== ''
              ? e.target.value.padEnd(4, '0')
              : e.target.value;
          const obj = {
            question,
            inputType,
            answer: [`${newYear}-${month}-${day}`],
            require: need,
          };
          setter(obj, num);
        }}
      />
      <span className="birthText">&nbsp;年&nbsp;</span>
      <input
        className="hide-spin"
        type="text"
        name="month"
        maxLength="2"
        style={{ width: '4rem' }}
        value={month}
        ref={monthRef}
        inputMode="numeric"
        onChange={(e) => {
          if (/^\d*$/.test(e.target.value)) {
            const obj = {
              question,
              inputType,
              answer: [`${year}-${e.target.value}-${day}`],
              require: need,
            };
            setter(obj, num);
            if (e.target.value.length === 2) {
              dayRef.current.focus();
            } else if (e.target.value.length === 0) {
              yearRef.current.focus();
            }
          }
        }}
        onBlur={(e) => {
          const month =
            e.target.value.length === 1 && e.target.value !== ''
              ? `0${e.target.value}`
              : e.target.value;
          const obj = {
            question,
            inputType,
            answer: [`${year}-${month}-${day}`],
            require: need,
          };
          setter(obj, num);
        }}
      />
      <span className="birthText">&nbsp;月&nbsp;</span>
      <input
        className="hide-spin"
        type="text"
        name="date"
        maxLength="2"
        style={{ width: '4rem' }}
        value={day}
        ref={dayRef}
        inputMode="numeric"
        onChange={(e) => {
          if (/^\d*$/.test(e.target.value)) {
            intoSetter(e.target.value);
            if (e.target.value.length === 0) {
              monthRef.current.focus();
            }
          }
        }}
        onBlur={(e) => {
          const day =
            e.target.value.length === 1 && e.target.value !== ''
              ? `0${e.target.value}`
              : e.target.value;
          intoSetter(day);
        }}
      />
      <span className="birthText">&nbsp;日&nbsp;</span>
    </div>
  );
}
DateArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  inputType: PropTypes.number,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * ラジオボタン
 * @param {object} 質問、必須フラグ、回答、形式、選択肢、配列の番号、セッター
 * @returns ラジオボタンのHTML
 */
function RadioArea({ question, need, inputType, selection, num, setter, answer }) {
  const [...itemArray] = selection;

  // ラジオボタンを構成する。
  const selectArray = [];
  for (let i = 0; i < itemArray.length; i += 1) {
    selectArray.push(
      <label
        htmlFor={`radio_${num}_${i}`}
        className="customRadio"
        key={`radio${i + 1}`}
        id="reserveRadio"
      >
        <input
          id={`radio_${num}_${i}`}
          type="radio"
          onChange={() => {
            const obj = { question, inputType, answer: [selection[i]], require: need };
            setter(obj, num);
          }}
          name={`radio_${num}`}
          checked={answer[0] === selection[i]}
        />
        {itemArray[i]}
      </label>
    );
  }

  return <div>{selectArray}</div>;
}
RadioArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  inputType: PropTypes.number,
  selection: PropTypes.array,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * チェックボックス
 * @param {object} 質問、必須フラグ、回答、形式、選択肢、配列の番号、セッター
 * @returns チェックボックスのHTML
 */
function CheckBoxArea({ question, need, answer, inputType, selection, num, setter }) {
  const [...multiArray] = selection;

  // チェックボックス表示を構成する。
  const selectList = [];

  // チェックボックスにチェックをつける。
  const plusCheck = (event) => {
    const { value } = event.target;
    let copyAnswer = [...answer];

    // 最初の空文字を削除する。
    if (copyAnswer[0] === '') {
      copyAnswer = [];
    }

    const itemNumber = copyAnswer.indexOf(value);
    if (itemNumber === -1) {
      copyAnswer.push(value);
      // selectionの表示順でフィルタリングし、並び順を画面の選択肢と一致させる
      copyAnswer = selection.filter((item) => copyAnswer.includes(item));
    } else {
      copyAnswer.splice(itemNumber, 1);
    }

    const obj = { question, inputType, answer: copyAnswer, require: need };
    setter(obj, num);
  };

  for (let i = 0; i < multiArray.length; i += 1) {
    selectList.push(
      <div key={`checkBox_${num}_${i}`} id="reserveCheckBox" className="customCheckBox">
        <input
          type="checkbox"
          id={`checkBox_${num}_${i}`}
          value={multiArray[i]}
          onChange={plusCheck}
          checked={answer.includes(multiArray[i])}
        />
        <label htmlFor={`checkBox_${num}_${i}`} value={answer}>
          {multiArray[i]}
        </label>
      </div>
    );
  }

  return <div>{selectList}</div>;
}
CheckBoxArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  inputType: PropTypes.number,
  selection: PropTypes.array,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * セレクトボックス
 * @param {object} 質問、必須フラグ、回答、形式、選択肢、配列の番号、セッター
 * @returns セレクトボックスのHTML
 */
function SelectBoxArea({ question, need, answer, inputType, selection, num, setter }) {
  const selectItems = [
    <option value="" key="selection">
      選択してください。
    </option>,
  ];
  for (let i = 0; i < selection.length; i += 1) {
    selectItems.push(
      <option value={`${selection[i]}`} key={`selection${i + 1}`}>
        {selection[i]}
      </option>
    );
  }

  // 選んだアイテムをstateに入れる。
  const selectedItem = (event) => {
    const { value } = event.target;
    const obj = { question, inputType, answer: value ? [value] : [], require: need };
    setter(obj, num);
  };

  return (
    <select name="item" id="item-select" onChange={selectedItem} value={answer?.[0] || ""}>
      {selectItems}
    </select>
  );
}
SelectBoxArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  inputType: PropTypes.number,
  selection: PropTypes.array,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * 電話番号欄
 * @param {object} 質問、必須フラグ、回答、プレースホルダー、形式、配列の番号、セッター
 * @returns 電話番号欄のHTML
 */
function TellNumArea({ question, need, answer, placeholder, inputType, num, setter }) {
  return (
    <input
      type="text"
      value={answer[0]}
      name={`${inputType}`}
      placeholder={placeholder}
      inputMode="numeric"
      onChange={(e) => {
        const obj = { question, inputType, answer: [e.target.value], require: need };
        setter(obj, num);
      }}
    />
  );
}
TellNumArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  placeholder: PropTypes.string,
  inputType: PropTypes.number,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * 数字のみ
 * @param {object} 質問、必須フラグ、回答、プレースホルダー、形式、配列の番号、セッター
 * @returns 数字のみ欄のHTML
 */
function MathOnlyArea({ question, need, answer, placeholder, inputType, num, setter }) {
  return (
    <input
      type="text"
      value={answer[0]}
      name={`${inputType}`}
      placeholder={placeholder}
      inputMode="numeric"
      onChange={(e) => {
        const obj = { question, inputType, answer: [e.target.value], require: need };
        setter(obj, num);
      }}
    />
  );
}
MathOnlyArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  placeholder: PropTypes.string,
  inputType: PropTypes.number,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * 英数字欄
 * @param {object} 質問、必須フラグ、回答、プレースホルダー、形式、配列の番号、セッター
 * @returns 英数字欄のHTML
 */
function EngMathArea({ question, need, answer, placeholder, inputType, num, setter }) {
  return (
    <input
      type="text"
      value={answer[0]}
      name={`${inputType}`}
      placeholder={placeholder}
      inputMode="latin"
      onChange={(e) => {
        const obj = { question, inputType, answer: [e.target.value], require: need };
        setter(obj, num);
      }}
    />
  );
}
EngMathArea.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  answer: PropTypes.array,
  placeholder: PropTypes.string,
  inputType: PropTypes.number,
  num: PropTypes.number,
  setter: PropTypes.func,
}.isRequired;

/**
 * 外部URL欄
 * @param {object}
 * @returns 英数字欄のHTML
 */
function UrlArea({ url, urlText }) {
  return (
    <div>
      <a href={url} target="_blank" rel="noreferrer">
        {urlText !== '' ? urlText : url}
      </a>
    </div>
  );
}
UrlArea.propTypes = {
  url: PropTypes.string,
  utlText: PropTypes.string,
}.isRequired;

/**
 * 各項目に共通する項目
 * @param {object} 質問、必須フラグ、説明、HTML
 * @returns 表示するHTMLを返す
 */
function CustomParts({ question, need, explanation, html }) {
  return (
    <dl className="custom">
      <dt>
        {question}&nbsp;
        {need ? <span className="required">*必須</span> : null}
      </dt>
      <dd>
        {explanation !== '' ? explanation : null}
        {html}
      </dd>
    </dl>
  );
}
CustomParts.propTypes = {
  question: PropTypes.string,
  need: PropTypes.bool,
  explanation: PropTypes.string,
  html: PropTypes.element,
}.isRequired;

export {
  TextBox,
  TextArea,
  DateArea,
  RadioArea,
  CheckBoxArea,
  SelectBoxArea,
  TellNumArea,
  MathOnlyArea,
  EngMathArea,
  UrlArea,
};
export default CustomParts;
